import { useRef, useEffect } from 'react';
import { useHeatmap } from '../../hooks/useHeatmap';

interface HeatmapViewerProps {
  url: string;
  type: 'click' | 'scroll' | 'move' | 'rage';
  device: string;
  dateRange: string;
}

export default function HeatmapViewer({ url, type, device, dateRange }: HeatmapViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { data, isLoading } = useHeatmap({ url, type, dateRange, device });

  // Normalize points from API response
  // API returns: { points: [{x, y, count}] } for click/rage
  //              { positions: [{x, y}] } for move
  //              { distribution: [{depth, count}] } for scroll
  const points = (data as any)?.points || (data as any)?.positions || [];
  const totalInteractions = points.reduce(
    (sum: number, p: any) => sum + (p.count || p.value || 1),
    0,
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !points || points.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, rect.width, rect.height);

    // Draw white page background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Find max coordinates to determine page dimensions
    const maxX = Math.max(...points.map((p: any) => Number(p.x) || 0), 1);
    const maxY = Math.max(...points.map((p: any) => Number(p.y) || 0), 1);
    const maxCount = Math.max(...points.map((p: any) => Number(p.count) || Number(p.value) || 1), 1);

    // Scale points to fit canvas
    const scaleX = rect.width / Math.max(maxX * 1.1, rect.width);
    const scaleY = rect.height / Math.max(maxY * 1.1, rect.height);

    points.forEach((point: any) => {
      const rawX = Number(point.x);
      const rawY = Number(point.y);
      const rawCount = Number(point.count) || Number(point.value) || 1;

      // Skip invalid points
      if (!isFinite(rawX) || !isFinite(rawY) || !isFinite(rawCount)) return;

      const x = rawX * scaleX;
      const y = rawY * scaleY;
      const intensity = rawCount / maxCount;
      const radius = Math.max(15 + intensity * 35, 5);

      // Guard against non-finite gradient params
      if (!isFinite(x) || !isFinite(y) || !isFinite(radius) || radius <= 0) return;

      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);

      if (type === 'rage') {
        gradient.addColorStop(0, `rgba(239, 68, 68, ${intensity * 0.8})`);
        gradient.addColorStop(0.5, `rgba(239, 68, 68, ${intensity * 0.3})`);
        gradient.addColorStop(1, 'rgba(239, 68, 68, 0)');
      } else {
        const hue = (1 - intensity) * 240;
        gradient.addColorStop(0, `hsla(${hue}, 100%, 50%, ${intensity * 0.8})`);
        gradient.addColorStop(0.5, `hsla(${hue}, 100%, 50%, ${intensity * 0.3})`);
        gradient.addColorStop(1, `hsla(${hue}, 100%, 50%, 0)`);
      }

      ctx.beginPath();
      ctx.fillStyle = gradient;
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    });
  }, [data, type, points]);

  if (isLoading) {
    return (
      <div className="card flex h-96 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  return (
    <div className="card">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            {type.charAt(0).toUpperCase() + type.slice(1)} Heatmap
          </h3>
          <p className="text-xs text-gray-500">
            {(totalInteractions || 0).toLocaleString()} total interactions
          </p>
        </div>
      </div>
      <div className="relative overflow-hidden rounded-lg border border-gray-200">
        <canvas
          ref={canvasRef}
          className="h-[600px] w-full"
          style={{ display: 'block' }}
        />
        {points.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80">
            <p className="text-sm text-gray-500">No heatmap data available for this page</p>
          </div>
        )}
      </div>
    </div>
  );
}
